import { useRef, useState } from 'react';
import { Button, Icon, Image, InlineStack, Paragraph, Stack, Text } from '@customerio/pluma-components/react';
import { FileInput } from '@/components/file-input';
import { getUploadFunction } from '@/services/uploads';
import { useSignCollection } from '@/services/db/collection/mutations';
import { useAnalytics } from '@/services/analytics';
import { isEmptyString } from '@/utils/string';
import { toSignResponse, type ImportSource, type SheetSource } from './collection-payload';
import { GoogleSheetsSourceButton } from './google-sheets-source-button';
import googleSheetsLogo from '@/assets/images/integrations/google-sheets.svg';

const ACCEPTED_EXTENSIONS = ['csv', 'json'];
const EXTENSION_TO_MIME: Record<string, string> = { csv: 'text/csv', json: 'application/json' };
export const FILE_INPUT_LABEL = 'Drag and drop CSV or JSON file here';

type ImportSourceFieldProps = {
  value: ImportSource | null;
  onChange: (source: ImportSource | null) => void;
  workspaceId: number;
  maxFileSizeBytes: number | undefined;
  /**
   * Notifies the owner while an upload is in flight. During a replace, `value`
   * still points at the previous key, so a form that only checks for a non-null
   * source would submit stale import data.
   */
  onUploadingChange: ((isUploading: boolean) => void) | undefined;
};

function fileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? '';
}

// The collections endpoints only ever return the sheet ids (ui_api/collections/index.go), so the
// display names exist solely on a source picked in this session.
function describeSheet(sheet: SheetSource): string {
  if (isEmptyString(sheet.sheet_name) || isEmptyString(sheet.spreadsheet_name)) {
    return `Sheet ID ${sheet.spreadsheet_id}`;
  }
  return `${sheet.sheet_name} from Google Sheet ${sheet.spreadsheet_name}`;
}

// Decimal units, matching how the limits themselves are expressed (10 * 1000 * 1000) — a
// binary divisor would label the 10 MB default as "9 MB". Floored, not rounded, because
// enforcement compares the exact byte count, so rounding up would advertise a maximum the
// field then rejects. Sub-megabyte limits drop to KB rather than rendering a bogus "0 MB".
function maxFileSizeLabel(maxFileSizeBytes: number): string {
  if (maxFileSizeBytes < 1_000_000) {
    return `${Math.floor(maxFileSizeBytes / 1_000)} KB`;
  }

  return `${Math.floor(maxFileSizeBytes / 1_000_000)} MB`;
}

export function ImportSourceField({
  value,
  onChange,
  workspaceId,
  maxFileSizeBytes,
  onUploadingChange,
}: ImportSourceFieldProps) {
  const sign = useSignCollection({ workspaceId });
  const analytics = useAnalytics();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateIsUploading = (next: boolean) => {
    setIsUploading(next);
    onUploadingChange?.(next);
  };
  // Bumped whenever the source changes, so an upload that resolves after the
  // user picked a sheet (or cleared the field) can't overwrite the newer choice.
  const sourceId = useRef(0);

  // Superseding an upload transfers ownership of the uploading flag: the old
  // upload's `finally` no longer clears it, so whatever superseded it must.
  const supersedeSource = () => {
    sourceId.current += 1;
    updateIsUploading(false);
    setError(null);
  };

  const handleSheetSelected = (sheet: SheetSource) => {
    supersedeSource();
    onChange({ kind: 'sheet', sheet });
  };

  const clearSource = () => {
    supersedeSource();
    onChange(null);
  };

  const handleFileSelected = async (file: File, viaDragAndDrop: boolean) => {
    const extension = fileExtension(file.name);
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      setError('Choose a CSV or JSON file');
      return;
    }
    // Only reject once the workspace limit is known. Standing in a default here would cap
    // uploads below what the workspace actually allows whenever the settings request is
    // still in flight or has failed; the sign endpoint enforces the real limit either way.
    if (maxFileSizeBytes != null && file.size > maxFileSizeBytes) {
      setError(`File is too large. The maximum size is ${maxFileSizeLabel(maxFileSizeBytes)}.`);
      return;
    }
    // Tracked only once the file clears validation, so rejected picks don't inflate the
    // upload count against the uploads that actually start.
    void analytics.track('import', 'uploadStarted', { source: 'from_disk', dragAndDrop: viaDragAndDrop });
    const currentSourceId = sourceId.current + 1;
    sourceId.current = currentSourceId;
    setError(null);
    updateIsUploading(true);
    try {
      const contentType = EXTENSION_TO_MIME[extension] ?? file.type;
      const signature = await sign.mutateAsync({ size: String(file.size), type: contentType });
      const { uploadFn, data } = getUploadFunction(toSignResponse(signature, contentType));
      const { key } = await uploadFn(file, data, {});
      if (sourceId.current !== currentSourceId) return;

      onChange({ kind: 's3', s3Import: key, filename: file.name });
    } catch {
      if (sourceId.current !== currentSourceId) return;

      setError("File couldn't be uploaded. Try again.");
    } finally {
      // A superseded upload must not report "done" — a newer upload may still be
      // running, and clearing the flag here would unlock submit against the older
      // source. Whoever superseded this one already cleared it.
      if (sourceId.current === currentSourceId) updateIsUploading(false);
    }
  };

  if (value != null && value.kind === 's3') {
    return (
      <FileInput
        label={`Replace ${value.filename}`}
        accept=".csv,.json"
        isDisabled={isUploading}
        error={error ?? undefined}
        onFileSelected={(file, { viaDragAndDrop }) => void handleFileSelected(file, viaDragAndDrop)}
        variant="plain"
      >
        {({ openFilePicker }) => (
          <Stack gap="200">
            <InlineStack alignBlock="center" gap="150">
              <InlineStack alignBlock="center" gap="100" backgroundColor="subtle" p="200">
                <Icon name="download" />
                <Text>{value.filename}</Text>
              </InlineStack>
              <Icon name="check" color="success" />
            </InlineStack>
            <InlineStack gap="100">
              <Button variant="secondary" isDanger={true} isDisabled={isUploading} onClick={clearSource}>
                Delete file
              </Button>
              <Button variant="secondary" isDisabled={isUploading} onClick={openFilePicker}>
                Replace
              </Button>
            </InlineStack>
          </Stack>
        )}
      </FileInput>
    );
  }

  if (value != null && value.kind === 'sheet') {
    return (
      <Stack gap="100">
        <InlineStack alignBlock="center" gap="050">
          <Image src={googleSheetsLogo} alt="" width={16} height={16} />
          <Paragraph>{describeSheet(value.sheet)}</Paragraph>
        </InlineStack>

        <InlineStack gap="100">
          <GoogleSheetsSourceButton workspaceId={workspaceId} onSelect={handleSheetSelected}>
            Replace
          </GoogleSheetsSourceButton>
          <Button variant="tertiary" onClick={clearSource}>
            Remove
          </Button>
        </InlineStack>
      </Stack>
    );
  }

  return (
    <Stack gap="100">
      <FileInput
        label={FILE_INPUT_LABEL}
        browseLabel="Choose file"
        accept=".csv,.json"
        isDisabled={isUploading}
        error={error ?? undefined}
        onFileSelected={(file, { viaDragAndDrop }) => void handleFileSelected(file, viaDragAndDrop)}
        action={
          <GoogleSheetsSourceButton workspaceId={workspaceId} onSelect={handleSheetSelected} isDisabled={isUploading} />
        }
      />
      {maxFileSizeBytes != null ? (
        <Text color="subtle" size="xs">
          Maximum size: {maxFileSizeLabel(maxFileSizeBytes)}
        </Text>
      ) : null}
    </Stack>
  );
}
